/**
 * src/lib/shoppeCatalogue.js — WHAT THE BOOTH IS ALLOWED TO OFFER.
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ EXTRACTED FROM RenFestShop.jsx SO TWO SCREENS CANNOT DISAGREE. The Shoppe
 * renders this; the console's Shelf panel lets staff switch items off. If the
 * console held its own copy of the list, the day someone edited one and not the
 * other is the day an operator turns off a product a guest can still see.
 *
 * ⚠️ THIS IS DATA, NOT A COMPONENT. Nothing here imports React, so the console
 * chunk does not drag the booth's SVG vessels in behind it.
 *
 * ── ⚠️ THE CATALOGUE IS JURISDICTION-FILTERED. READ BEFORE EDITING. ────────
 * KC Renaissance Festival is in BONNER SPRINGS, KANSAS. Kansas scheduled
 * 7-hydroxymitragynine and mitragynine as Schedule I effective 2026-07-01
 * (HB 2365 / SB 430), and speciociliatine is captured as a diastereomer.
 *
 * Verified against `compliance_state_status` for KS on 2026-08-06. The
 * following are ILLEGAL in Kansas and MUST NOT appear in this catalogue, even
 * though they are among the highest-volume products in the company:
 *
 *   · 7-OH Chewable Tablets (White Vein)        — 7_oh, Schedule I 2026-07-01
 *   · 7-OH + Pseudoindoxyl Combo (Green Vein)   — contains 7-OH
 *   · Specio+ Tablets                           — speciociliatine
 *   · Rooted Kava + Alkaloid Shots              — alkaloid content, excluded
 *                                                 pending a formulation check
 *   · Any Delta-9 or HHC FLOWER, RAW MATERIAL or VAPE — form bans, K.S.A. 2-3908
 *
 * THCa flower and pre-rolls ARE legal in Kansas (delta-9-only basis) and are
 * included. Delta-8, Delta-9 edibles/drinks and CBN orals are `restricted`, not
 * illegal — per the compliance policy in CLAUDE.md, `restricted` is reference
 * data and not a blocker, so they stay.
 *
 * If this component is ever reused for an event in another state, this list is
 * WRONG and has to be re-derived from `compliance_state_status` for that state.
 * A hardcoded catalogue that silently outlives its jurisdiction is exactly the
 * failure the breach monitor exists to catch.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/* ⚠️ THE TERMINAL THIS BOOTH SELLS THROUGH — NOT THE BOOTH NUMBER.
   RenFest runs on POS ids 737 and 738 ("KC Corp Event 6 and 7"). The booth is
   #383, which is a different number entirely and has burned people before. */
export const POS_ID = '737';

/* ⚠️ WHICH BOOTH'S SHELF THIS IS. These two are the route — `/shaman/renfest-2026`
   is (tenant.slug, event.slug), sql/020 — and they are what the Shelf panel in
   the console writes against. If this component is ever reused for another
   event, these change with the catalogue above, together, or an operator in one
   tent switches off a product in another. */
export const TENANT_SLUG = 'shaman';
export const EVENT_SLUG = 'renfest-2026';

/**
 * Prices and names are the REAL ones from the production `products` table as of
 * 2026-08-06, so what a guest reads matches what the counter charges.
 *
 * ⚠️ This is a hardcoded curation, not a live feed, and that is deliberate for
 * a first pass — a booth catalogue is a merchandising decision, not everything
 * that happens to be published. When it moves to a live source it needs its own
 * curated table with an `event_id`, NOT a raw `products` query, or the whole
 * 857-row catalogue lands on a guest's phone.
 */
export const SECTIONS = [
  {
    k: 'tinctures',
    n: 'Tinctures & Elixirs',
    blurb: 'Drawn drop by drop. Placed beneath the tongue.',
    items: [
      { n: 'CBG Oil THC Free', d: '30mL - the clear-headed one', p: 17.49, pos: 388, v: 'bottle', c: '#8f7f2f', c2: '#544a1a' },
      { n: 'CBNight Water Soluble', d: '30mL - for the small hours', p: 99.95, pos: 349, v: 'bottle', c: '#4f4f8f', c2: '#2e2e54' },
      { n: 'D9 Day Water Soluble', d: '30mL - sun-up blend', p: 34.99, pos: 611, v: 'bottle', c: '#9f8f3f', c2: '#5e5425' },
      { n: 'D9 Night Water Soluble', d: '30mL - sun-down blend', p: 34.99, pos: 612, v: 'bottle', c: '#5f4f9f', c2: '#382e5e' },
      { n: 'Oil of Oregano', d: '30mL - the old herbal', p: 24.99, pos: 876, v: 'bottle', c: '#6f8f3f', c2: '#415425' },
      { n: 'Water Soluble Full Spectrum', d: '30mL · the flagship', p: 59.99, pos: 19, v: 'bottle', c: '#3f8f6b', c2: '#1f5a41' },
      { n: 'Extra Strength Water Soluble', d: '30mL · the strong stuff', p: 124.99, pos: 141, v: 'bottle', c: '#2f7f8f', c2: '#164a54' },
      /* `p` here is documentation, not what renders — RenFestShop overwrites it
         with the live register price (`p: row.cents / 100`). It said $34.99,
         the 150mg option, which is one of four sizes on #34 and all four are
         switched off. A guest already sees $59.99, the base. Corrected so the
         file stops disagreeing with the shelf. */
      { n: 'Hemp Oil Omega 3', d: 'daily wellness', p: 59.99, pos: 199, v: 'bottle', c: '#8f8f2f', c2: '#54541a' },
    ],
  },
  {
    k: 'confections',
    n: 'Confections & Chews',
    blurb: 'Sweetmeats for the merry.',
    items: [
      { n: 'CBG Gummies', d: 'cherry, thirty of them', p: 34.99, pos: 400, v: 'jar', c: '#9f4f5f', c2: '#5e2e38' },
      { n: 'Delta 8 Caramels', d: 'chewy and slow', p: 4.99, pos: 473, v: 'pouch', c: '#8f6f3f', c2: '#544125' },
      { n: 'Sample Pack of Gummies', d: 'a taste of everything', p: 14.95, pos: 26, v: 'pouch', c: '#7f8f4f', c2: '#4a542e' },
      { n: 'Shaman Shrooms Capsules', d: 'balance blend, 250 caps', p: 39.99, pos: 727, v: 'jar', c: '#6f5f4f', c2: '#41372e' },
      { n: 'Road Trip Microdose', d: 'orange, a gentler dose', p: 34.99, pos: 904, v: 'jar', c: '#9f7f3f', c2: '#5e4a25' },
      { n: 'Delta 9 Gummies', d: 'the crowd favourite', p: 29.99, pos: 529, v: 'jar', c: '#c0392b', c2: '#7a2018' },
      { n: 'CBNight Sleep Gummies', d: 'CBN · for the night watch', p: 49.95, pos: 501, v: 'jar', c: '#3f4f9f', c2: '#212c5e' },
      { n: 'Nano CBD Gummies', d: 'fast acting', p: 17.99, pos: 401, v: 'jar', c: '#c07a2b', c2: '#7a4a18' },
      { n: 'Delta 9 Caramel Chews', d: 'soft and slow', p: 29.99, pos: 537, v: 'pouch', c: '#8f5f2f', c2: '#54371a' },
      { n: 'Delta 9 Chocolates', d: 'a single piece', p: 4.99, pos: 519, v: 'pouch', c: '#5f3f2f', c2: '#39251a' },
      { n: 'Road Trip Mushroom Gummies', d: 'for the wanderer', p: 34.99, pos: 902, v: 'jar', c: '#7f4f8f', c2: '#4a2b54' },
      { n: 'Delta 8 Nano Gummies', d: 'gentler kin', p: 14.99, pos: 471, v: 'jar', c: '#8f7f2f', c2: '#544a1a' },
    ],
  },
  {
    k: 'draughts',
    n: 'Draughts & Potions',
    blurb: 'Poured cold. Drunk in the shade.',
    items: [
      { n: 'High Days THC Soda', d: 'cream or watermelon', p: 6.99, pos: 994, v: 'flask', c: '#9f5f7f', c2: '#5e3849' },
      { n: 'Reframe Cannabis Spirit', d: '750mL - pour it like wine', p: 55.00, pos: 1066, v: 'flask', c: '#7f3f5f', c2: '#4a2538' },
      { n: 'TORCH THC Seltzer', d: 'the booth bestseller', p: 7.99, pos: 841, v: 'flask', c: '#2f8f9f', c2: '#16545e' },
      { n: 'CBD Sparkling Tea', d: 'cool and clear', p: 9.99, pos: 353, v: 'flask', c: '#9f8f3f', c2: '#5e5421' },
      { n: 'High9ine Delta-9 Beverage', d: 'a proper draught', p: 24.99, pos: 730, v: 'flask', c: '#9f3f5f', c2: '#5e2138' },
      { n: 'High Days THC Soda', d: 'sweet and fizzing', p: 6.99, pos: null, v: 'flask', c: '#c0562b', c2: '#7a3418' },
      { n: 'STIIIZY Hemp Seltzer', d: 'light and dry', p: 5.99, pos: 832, v: 'flask', c: '#3f9f7f', c2: '#215e4a' },
      { n: 'Rocket Fuel Sparkling Soda', d: 'for the bold', p: 34.99, pos: null, v: 'flask', c: '#9f5f2f', c2: '#5e381a' },
    ],
  },
  {
    k: 'salves',
    n: 'Salves & Unguents',
    blurb: 'Worked into the aching parts.',
    items: [
      { n: 'CBD Topical Serum', d: '15mL - the concentrated one', p: 89.99, pos: 11, v: 'tin', c: '#8f8f6f', c2: '#545441' },
      { n: 'Signature Topical Cream', d: '1oz - 1000mg and up', p: 69.99, pos: 544, v: 'tin', c: '#8f7f6f', c2: '#544a41' },
      { n: 'CBD Lip Balm', d: 'for a windy field', p: 10.00, pos: 16, v: 'tin', c: '#9f8f7f', c2: '#5e544a' },
      /* ⚠️ THE 10 YEAR TIN ONLY, NOT THE 250mg BASE AND NOT THE 2000mg.
         Joel, 2026-09-05, standing at the booth: "we need the ten year one
         listed at the forty one ninety nine ... Only 10 year topical cream."

         ⚠️ THE ORDER IS NOT THE WHOLE TRUTH, AND THAT IS THE LESSON HERE. This
         shelf was rebuilt from RenFest order 4312820 and everything not on that
         order was cut, which is a good rule that got one thing wrong: the 10
         Year is not on the order and the booth physically has it. Joel: "it
         didn't see the tenure, but we did get tenure product." Stock that
         arrived outside the order still sells. Check the order AND ask the
         booth.

         ⚠️ OPTION 1356 ON PRODUCT 151, group "10yr Anniversary Collection",
         base minus $18.00 = $41.99. `opt` is what prices it off its own option
         row rather than the parent's $59.99.

         ⚠️ IT DISAPPEARS IF THE OPTION IS NOT `pos_enabled` ON THE TERMINAL.
         `flat` drops a curated item whose option the live feed has no row for,
         deliberately, so the shelf can never quote a price the till refuses.
         Alyssa enabled it on 737. If this tin vanishes from the Shoppe, that
         switch is the first thing to check, not this file. */
      { n: '10 Year Anniversary Cream', d: '250mg - the decade tin', p: 41.99, pos: 151, opt: 1356, v: 'tin', c: '#6f7f5f', c2: '#414a37' },
    ],
  },
  {
    k: 'beasts',
    n: 'For the Beasts',
    blurb: 'Hounds, hawks and the household menagerie.',
    items: [
      { n: 'CBD Oil for Cats', d: 'for the aloof ones', p: 59.99, pos: 23, v: 'bottle', c: '#7f6f9f', c2: '#4a415e' },
      { n: 'CBD Pet Sample Kit', d: 'a box to try', p: 20.00, pos: 1102, v: 'pouch', c: '#6f8f7f', c2: '#415449' },
      { n: 'Feather Guard', d: 'for the feathered ones', p: 39.99, pos: 879, v: 'bottle', c: '#8f9f6f', c2: '#545e41' },
      { n: 'Soft CBD Dog Chews', d: 'for the good hound', p: 24.99, pos: 195, v: 'pouch', c: '#7f5f3f', c2: '#4a3725' },
      { n: 'CBD Chews for Dogs', d: 'larger tin', p: 44.99, pos: 153, v: 'pouch', c: '#5f7f3f', c2: '#374a25' },
      { n: 'Canine Hemp Oil Tincture', d: 'drops in the bowl', p: 59.99, pos: 20, v: 'bottle', c: '#3f7f5f', c2: '#254a37' },
      { n: 'Large Dog Treats', d: 'for the great hound', p: 49.95, pos: 615, v: 'pouch', c: '#8f6f4f', c2: '#54412f' },
    ],
  },
  {
    k: 'sundries',
    n: 'Sundries & Smokes',
    blurb: 'Everything else the fair may require.',
    items: [
      /* SUNDRIES IS ONE LINE ON PURPOSE. Joel cut this shelf at the booth on
         2026-09-05: no Discount Flower (#767 $10.00), no Discount Pre-Roll
         (#770 $4.20), no THCa Pre-Rolls (#546 $7.49). Blue Lotus only.
         History, so nobody re-derives the removed rows from the paperwork:
         #588 THCa Flower and #549 THCa Diamond Pre-Rolls came off first,
         because the shelf said $19.99 while the only sellable strain rang at
         $24.99 (74 of 75 variants switched off) and neither product is on the
         RenFest order (Shaman Intelligence order_items id 4312820, 45 distinct
         products). #767 and #770 ARE on that order, so they were added as the
         honest flat-price replacements. Joel then pulled them anyway. The order
         says what was shipped to the tent; it does not say what he wants on the
         menu, and he decides the menu.
         Do not re-add any of 588, 549, 767, 770 or 546 from an order export.
         Ask Joel. */
      { n: 'Blue Lotus Pre Roll', d: 'a bloom for the dreamers', p: 9.99, pos: 1115, v: 'bundle', c: '#5f7fbf', c2: '#374a70' },
    ],
  },
];
